import { useRef, useCallback, useEffect } from 'react'

type Callback<T extends string | number | unknown[]> = (value: T) => void

export function useThrottledDistinctCallback<
  T extends string | number | unknown[],
>(delay: number = 500) {
  const lastCall = useRef<number>(0)
  const lastKey = useRef<string>()
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const throttle = useCallback(
    (value: T, callback: Callback<T>) => {
      // Serialize dependencies as a key
      const key = JSON.stringify(value)
      if (lastKey.current === key) return
      lastKey.current = key

      const now = Date.now()
      const invoke = () => callback(value)

      if (lastCall.current === 0) {
        lastCall.current = now
        invoke()
        return
      }

      const remaining = delay - (now - lastCall.current)

      if (remaining <= 0) {
        if (timer.current) {
          clearTimeout(timer.current)
          timer.current = null
        }
        lastCall.current = now
        invoke()
      } else {
        if (timer.current) clearTimeout(timer.current)
        timer.current = setTimeout(() => {
          lastCall.current = Date.now()
          invoke()
        }, remaining)
      }
    },
    [delay],
  )

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    [],
  )

  return throttle
}
